// ============================================================
// TEMP LEDGER MODULE
// src/TempLedger.js
// Standalone migration ledger: old-system Sales register + Receivables
// upload, opening balances, conflict review, printable ledger statement.
// Fully isolated from historical_transactions / customer_opening_balances /
// Recon — does not read or write any of the app's real accounting tables.
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import { useAuth } from "./Auth";
import { parseCSV, fuzzyMatch, parseDate } from "./HistoricalImport";
import { fmtDMY } from "./dateUtils";
import { matchItems } from "./matchItems";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const COMPANY = {
  name:    "Super Foto DG X Press",
  address: "70, 3rd Floor, 4th Main, KSSIDC, Rajajinagar Industrial Estate, Bengaluru 560010",
};

const fmt = (n) => "₹" + parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});

// ── SHARED STYLES (mirrors HistoricalImport.js) ────────────────
const S = {
  pageHeader: {display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:28,paddingBottom:20,borderBottom:"1px solid var(--paper3)"},
  pageTitle:  {fontFamily:"var(--serif)",fontSize:29,fontWeight:300,letterSpacing:"-0.5px"},
  subtitle:   {fontSize:12,color:"var(--ink3)",marginTop:6},
  btnPrimary: {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"none",background:"var(--accent)",color:"#fff",fontWeight:500},
  btnGhost:   {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"},
  btnSm:      {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)"},
  btnDanger:  {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--danger-l)",background:"transparent",color:"var(--danger)"},
  card:       {background:"#fff",borderRadius:10,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"},
  statCard:   (c) => ({background:"#fff",borderRadius:10,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)",borderLeft:`3px solid ${c}`}),
  th:         (r) => ({padding:"10px 16px",textAlign:r?"right":"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,whiteSpace:"nowrap"}),
  td:         {padding:"12px 16px",fontSize:13,color:"var(--ink)"},
  input:      {padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%"},
  label:      {fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,display:"block",marginBottom:5},
  overlay:    {position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
  section:    {background:"#fff",borderRadius:10,padding:24,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",marginBottom:20},
};

// ── AUTOCOMPLETE (mirrors App.js) ───────────────────────────────
function AutoComplete({ value, onChange, onSelect, items, placeholder, renderItem, getKey, getLabel }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1); // keyboard-highlighted row
  const ref = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const { list: filtered, total: matchTotal } = matchItems(items, value, getLabel, 50);
  const act = active < filtered.length ? active : -1;
  useEffect(() => {
    if (act >= 0 && listRef.current) listRef.current.children[act]?.scrollIntoView({ block: "nearest" });
  }, [act]);
  const pick = (item) => { onSelect(item); setOpen(false); setActive(-1); };
  // Arrow keys move through the suggestions, Enter picks the highlighted
  // one, Esc/Tab close the list, so entry never needs the mouse.
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(filtered.length ? 0 : -1); }
      else if (filtered.length) setActive(a => (a + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open && filtered.length) setActive(a => (a <= 0 ? filtered.length - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (open && act >= 0 && filtered[act]) { e.preventDefault(); pick(filtered[act]); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); setOpen(false); setActive(-1); }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };
  return (
    <div className="autocomplete-wrap" ref={ref}>
      <input value={value||""} onChange={e=>{onChange(e.target.value);setOpen(true);setActive(-1);}} onFocus={()=>setOpen(true)} onKeyDown={onKeyDown} placeholder={placeholder} style={S.input}/>
      {open && filtered.length > 0 && (
        <div className="autocomplete-dropdown" ref={listRef}>
          {filtered.map((item, idx) => (
            <div key={getKey(item)} className={"autocomplete-item"+(idx===act?" active":"")} onMouseDown={()=>pick(item)}>
              {renderItem(item)}
            </div>
          ))}
          {matchTotal > filtered.length && (
            <div style={{padding:"8px 12px",fontSize:11,color:"var(--ink3)"}}>Showing first {filtered.length} of {matchTotal} — keep typing to narrow</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── HELPERS ───────────────────────────────────────────────────
function chunk(arr, size) { const out=[]; for (let i=0;i<arr.length;i+=size) out.push(arr.slice(i,i+size)); return out; }

async function fetchAllPaged(queryFactory) {
  let all=[]; let from=0; const pageSize=1000;
  while (true) {
    const { data, error } = await queryFactory().range(from, from+pageSize-1);
    if (error) throw error;
    if (!data || data.length===0) break;
    all = all.concat(data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

const normalizeName = (s) => (s||"").toLowerCase().trim().replace(/[&./-]/g," ").replace(/\s+/g," ").trim();

function stripCrSuffix(name) { return (name||"").replace(/[-–]{1,2}\s*cr\.?\s*$/i,"").trim(); }

function parseInvoiceSerial(str) {
  const m = String(str||"").trim().match(/^(\d+)/);
  return m ? parseInt(m[1],10) : null;
}

function detectHeaderStart(text, requiredTokens) {
  const lines = text.split(/\r?\n/);
  const idx = lines.findIndex(l => {
    const low = l.toLowerCase();
    return requiredTokens.every(t => low.includes(t));
  });
  return idx < 0 ? 0 : idx;
}

const numVal = (v) => { const n = parseFloat(String(v||"0").replace(/[,₹\s]/g,"")); return isNaN(n) ? 0 : n; };

async function loadActiveCustomers() {
  return fetchAllPaged(() => supabase.from("customers").select("id,name,phone").eq("is_active",true).order("name"));
}

async function loadXlsxOrCsvText(file) {
  if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
    if (!window.XLSX) {
      await new Promise((res,rej) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
        s.onload = res; s.onerror = rej; document.head.appendChild(s);
      });
    }
    const buf = await file.arrayBuffer();
    const wb = window.XLSX.read(new Uint8Array(buf),{type:"array"});
    const ws = wb.Sheets[wb.SheetNames[0]];
    return window.XLSX.utils.sheet_to_csv(ws);
  }
  return await file.text();
}

// Resolves raw display names against temp_ledger_customer_map, falling back
// to fuzzyMatch. Auto-resolved entries (mapped or fuzzy-matched) are persisted
// immediately so future uploads don't need to re-resolve the same name.
async function resolveNames(rawNames, customers) {
  const uniqueRaw = [...new Set(rawNames.map(n => (n||"").trim()).filter(Boolean))];
  const norms = uniqueRaw.map(normalizeName);
  const mapByNorm = {};
  for (const c of chunk(norms,200)) {
    if (!c.length) continue;
    const { data } = await supabase.from("temp_ledger_customer_map").select("*").in("normalized_name", c);
    (data||[]).forEach(m => { mapByNorm[m.normalized_name] = m; });
  }
  const byNorm = {};
  const newMappings = [];
  uniqueRaw.forEach((raw,i) => {
    const norm = norms[i];
    const existing = mapByNorm[norm];
    if (existing) {
      byNorm[norm] = { customerId: existing.customer_id, resolution: existing.resolution, matchedBy: existing.matched_by, raw };
      return;
    }
    const match = fuzzyMatch(raw, customers);
    if (match) {
      byNorm[norm] = { customerId: match.id, resolution: "matched", matchedBy: "auto", raw };
      newMappings.push({ raw_name: raw, normalized_name: norm, customer_id: match.id, resolution: "matched", matched_by: "auto" });
    } else {
      byNorm[norm] = { customerId: null, resolution: "unresolved", matchedBy: null, raw };
    }
  });
  for (const c of chunk(newMappings,200)) {
    if (!c.length) continue;
    await supabase.from("temp_ledger_customer_map").upsert(c, { onConflict: "normalized_name" });
  }
  return byNorm;
}

async function persistManualResolution(norm, raw, customerId) {
  await supabase.from("temp_ledger_customer_map").upsert({
    raw_name: raw, normalized_name: norm, customer_id: customerId,
    resolution: customerId ? "matched" : "ignored", matched_by: "manual",
    updated_at: new Date().toISOString(),
  }, { onConflict: "normalized_name" });
}

// ── QUICK-ADD CUSTOMER (opened from an unresolved-name row during upload) ──
function QuickAddCustomerModal({ prefillName, onClose, onCreated, toast }) {
  const [name, setName]   = useState(prefillName||"");
  const [type, setType]   = useState("counter");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast("Customer name is required.","error");
    setSaving(true);
    try {
      const { data, error } = await supabase.from("customers")
        .insert({ name:name.trim(), type, phone:phone||null, is_active:true })
        .select().single();
      if (error) throw error;
      toast("Customer created.");
      onCreated(data);
    } catch(e) { toast("Error: "+e.message,"error"); }
    finally { setSaving(false); }
  };

  return (
    <div style={{...S.overlay, zIndex:400}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:420}}>
        <div style={{padding:"20px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={S.pageTitle}>New customer</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"16px 24px 24px"}}>
          <label style={{...S.label,display:"block",marginBottom:5}}>Customer name *</label>
          <input value={name} onChange={e=>setName(e.target.value)} autoFocus style={{...S.input,marginBottom:14}}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Type</label>
              <select value={type} onChange={e=>setType(e.target.value)} style={S.input}>
                <option value="counter">Counter</option>
                <option value="outstation">Outstation</option>
                <option value="tpf">TPF</option>
                <option value="prv">PRV</option>
              </select>
            </div>
            <div>
              <label style={{...S.label,display:"block",marginBottom:5}}>Phone</label>
              <input value={phone} onChange={e=>setPhone(e.target.value)} style={S.input}/>
            </div>
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":"💾 Create & use"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── UNRESOLVED NAMES PANEL (shared by both upload flows) ───────
function UnresolvedNamesPanel({ unresolved, customers, onResolved, onCustomerCreated, toast }) {
  const [pickers, setPickers] = useState({});
  const [addFor, setAddFor] = useState(null); // the unresolved entry {norm,raw,count} being quick-added
  if (!unresolved.length) return null;
  return (
    <div style={{marginBottom:16,padding:"14px 16px",background:"var(--warn-l)",border:"1px solid var(--warn)",borderRadius:8}}>
      <div style={{fontSize:13,fontWeight:500,color:"var(--warn)",marginBottom:10}}>
        ⚠ {unresolved.length} customer name{unresolved.length!==1?"s":""} in this file could not be matched. Resolve them once — future uploads will remember your choice.
      </div>
      {unresolved.map(u => (
        <div key={u.norm} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderTop:"1px solid rgba(0,0,0,0.06)"}}>
          <div style={{flex:"0 0 220px",fontSize:13,fontWeight:500}}>{u.raw} <span style={{color:"var(--ink3)",fontWeight:400}}>({u.count} row{u.count!==1?"s":""})</span></div>
          <div style={{flex:1,maxWidth:260}}>
            <AutoComplete
              value={pickers[u.norm]||""}
              onChange={v=>setPickers(p=>({...p,[u.norm]:v}))}
              onSelect={async c=>{ await persistManualResolution(u.norm,u.raw,c.id); onResolved(u.norm,c.id); }}
              items={customers}
              placeholder="Search customer…"
              getKey={c=>c.id}
              getLabel={c=>c.name}
              renderItem={c=><div style={{fontSize:13}}>{c.name}</div>}
            />
          </div>
          <button style={S.btnSm} onClick={()=>setAddFor(u)}>+ Add customer</button>
          <button style={S.btnSm} onClick={async ()=>{ await persistManualResolution(u.norm,u.raw,null); onResolved(u.norm,null); }}>Ignore</button>
        </div>
      ))}
      {addFor && (
        <QuickAddCustomerModal
          prefillName={addFor.raw}
          onClose={()=>setAddFor(null)}
          toast={toast}
          onCreated={async (cust)=>{
            onCustomerCreated(cust);
            await persistManualResolution(addFor.norm, addFor.raw, cust.id);
            onResolved(addFor.norm, cust.id);
            setAddFor(null);
          }}
        />
      )}
    </div>
  );
}

function unresolvedList(rows, byNorm) {
  const map = new Map();
  rows.forEach(r => {
    const nm = byNorm[r.norm];
    if (nm && nm.resolution === "unresolved") {
      const cur = map.get(r.norm) || { norm: r.norm, raw: nm.raw, count: 0 };
      cur.count++;
      map.set(r.norm, cur);
    }
  });
  return [...map.values()];
}

// ── CLASSIFICATION (pure — reused on initial load and after manual resolve) ─
function classifySalesRows(rows, byNorm, existingMap) {
  return rows.map(r => {
    const nm = byNorm[r.norm] || { customerId:null, resolution:"unresolved" };
    const key = `${r.isoDate}|${r.invoiceSerial}`;
    const existingRow = existingMap[key];
    let status;
    if (nm.resolution === "ignored") status = "ignored";
    else if (nm.resolution === "unresolved") status = "unmatched";
    else if (r.inFileDuplicate) status = "duplicate";
    else if (!existingRow) status = "ready";
    else if (parseFloat(existingRow.gross_total)===r.gross && existingRow.customer_id===nm.customerId) status = "duplicate";
    else status = "conflict";
    return { ...r, customerId: nm.customerId, existingId: existingRow?.id||null, existingGross: existingRow?parseFloat(existingRow.gross_total):null, status };
  });
}

function classifyReceivablesRows(rows, byNorm, existingRows) {
  const existingExactCount = {};
  const existingByDCM = {};
  existingRows.forEach(e => {
    const exact = `${e.txn_date}|${e.customer_id}|${e.amount}|${e.method_normalized}`;
    existingExactCount[exact] = (existingExactCount[exact]||0)+1;
    const dcm = `${e.txn_date}|${e.customer_id}|${e.method_normalized}`;
    (existingByDCM[dcm] = existingByDCM[dcm]||[]).push({ id:e.id, amount:parseFloat(e.amount) });
  });
  const withCust = rows.map(r => {
    const nm = byNorm[r.norm] || { customerId:null, resolution:"unresolved" };
    return { ...r, customerId: nm.customerId, resolution: nm.resolution, status: undefined };
  });
  const groups = {};
  withCust.forEach(r => {
    if (r.resolution !== "matched") return;
    const exact = `${r.isoDate}|${r.customerId}|${r.amount}|${r.methodNormalized}`;
    (groups[exact] = groups[exact]||[]).push(r);
  });
  Object.entries(groups).forEach(([exact,groupRows]) => {
    const existingCount = existingExactCount[exact]||0;
    const toInsert = Math.max(0, groupRows.length - existingCount);
    const parts = exact.split("|");
    const meth = parts[parts.length-1];
    const amtStr = parts[parts.length-2];
    const cust = parts[parts.length-3];
    const d = parts.slice(0,parts.length-3).join("|");
    const dcm = `${d}|${cust}|${meth}`;
    const differingExisting = (existingByDCM[dcm]||[]).filter(e => e.amount !== parseFloat(amtStr));
    groupRows.forEach((r,i) => {
      if (i >= groupRows.length - toInsert) {
        if (differingExisting.length > 0) {
          r.status = "conflict";
          r.conflictExistingIds = differingExisting.map(e=>e.id);
          r.conflictExistingAmounts = differingExisting.map(e=>e.amount);
        } else {
          r.status = "ready";
        }
      } else {
        r.status = "duplicate";
      }
    });
  });
  withCust.forEach(r => {
    if (!r.status) r.status = r.resolution==="ignored" ? "ignored" : r.resolution==="unresolved" ? "unmatched" : "ready";
  });
  return withCust;
}

const STATUS_BADGE = {
  ready:     { bg:"var(--accent-l)", color:"var(--accent)", label:"✓ Ready" },
  duplicate: { bg:"var(--paper2)",   color:"var(--ink3)",   label:"Already exists" },
  conflict:  { bg:"var(--danger-l)", color:"var(--danger)", label:"⚠ Conflict" },
  unmatched: { bg:"var(--warn-l)",   color:"var(--warn)",   label:"No match" },
  ignored:   { bg:"var(--paper2)",   color:"var(--ink3)",   label:"Ignored" },
};

function StatusBadge({ status }) {
  const b = STATUS_BADGE[status] || STATUS_BADGE.unmatched;
  return <span style={{display:"inline-block",padding:"2px 7px",borderRadius:10,fontSize:11,fontWeight:500,background:b.bg,color:b.color}}>{b.label}</span>;
}

// ── SALES UPLOAD MODAL ──────────────────────────────────────────
function SalesUploadModal({ onClose, onDone, toast }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [existingMap, setExistingMap] = useState({});
  const [byNorm, setByNorm] = useState({});
  const [parsedRows, setParsedRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [inFileDupCount, setInFileDupCount] = useState(0);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setFileName(file.name);
    try {
      const text = await loadXlsxOrCsvText(file);
      const startIdx = detectHeaderStart(text, ["invoice","particulars"]);
      const trimmedText = text.split(/\r?\n/).slice(startIdx).join("\n");
      const { headers, rows } = parseCSV(trimmedText);
      const findCol = (pred) => headers.find(h => pred(h.toLowerCase().trim()));
      const dateCol = findCol(h=>h==="date");
      const particularsCol = findCol(h=>h==="particulars");
      const invoiceCol = findCol(h=>h.startsWith("invoice"));
      const gstNoCol = findCol(h=>h==="gstno"||h==="gst no");
      const grossCol = findCol(h=>h==="gross total");
      const salesGstCol = findCol(h=>h.includes("sales gst"));
      const sgstCol = findCol(h=>h.startsWith("sgst"));
      const cgstCol = findCol(h=>h.startsWith("cgst"));
      const salesIgstCol = findCol(h=>h.includes("sales igst"));
      const igstCol = findCol(h=>h.startsWith("igst")&&!h.includes("sales"));

      const parsed = rows.map(row => {
        const isoDate = parseDate(dateCol?row[dateCol]:"");
        const invoiceNoRaw = (invoiceCol?row[invoiceCol]:"").trim();
        const invoiceSerial = parseInvoiceSerial(invoiceNoRaw);
        const rawName = stripCrSuffix(particularsCol?row[particularsCol]:"");
        return {
          isoDate, invoiceNoRaw, invoiceSerial, rawName,
          norm: normalizeName(rawName),
          gross: numVal(grossCol?row[grossCol]:0),
          gstNo: gstNoCol ? ((row[gstNoCol]||"").trim()||null) : null,
          salesGst18: salesGstCol ? (numVal(row[salesGstCol])||null) : null,
          sgst9: sgstCol ? (numVal(row[sgstCol])||null) : null,
          cgst9: cgstCol ? (numVal(row[cgstCol])||null) : null,
          salesIgst18: salesIgstCol ? (numVal(row[salesIgstCol])||null) : null,
          igst18: igstCol ? (numVal(row[igstCol])||null) : null,
          valid: !!(isoDate && invoiceSerial && rawName),
        };
      }).filter(r => r.isoDate || r.rawName || r.invoiceNoRaw);

      const validRows = parsed.filter(r => r.valid);
      if (!validRows.length) { toast("No valid rows found — check the file has Date/Particulars/Invoice No./Gross Total columns.","error"); setBusy(false); return; }

      // Collapse duplicate invoice numbers within the same file (protects the DB unique constraint)
      const seenKeys = new Set();
      let dupCount = 0;
      const deduped = [];
      validRows.forEach(r => {
        const key = `${r.isoDate}|${r.invoiceSerial}`;
        if (seenKeys.has(key)) { dupCount++; r.inFileDuplicate = true; return; }
        seenKeys.add(key);
        deduped.push(r);
      });
      setInFileDupCount(dupCount);

      const custs = customers.length ? customers : await loadActiveCustomers();
      if (!customers.length) setCustomers(custs);
      const nameMap = await resolveNames(deduped.map(r=>r.rawName), custs);

      const dates = deduped.map(r=>r.isoDate).sort();
      const minDate = dates[0], maxDate = dates[dates.length-1];
      const existing = minDate ? await fetchAllPaged(() => supabase.from("temp_ledger_sales")
        .select("id,txn_date,invoice_serial,gross_total,customer_id")
        .gte("txn_date",minDate).lte("txn_date",maxDate)) : [];
      const eMap = {};
      existing.forEach(e => { eMap[`${e.txn_date}|${e.invoice_serial}`] = e; });

      setRawRows(deduped);
      setExistingMap(eMap);
      setByNorm(nameMap);
      setParsedRows(classifySalesRows(deduped, nameMap, eMap));
      setStep(2);
    } catch(e) {
      toast("Failed to read file: "+e.message,"error");
    } finally { setBusy(false); }
  };

  const handleNameResolved = (norm, customerId) => {
    setByNorm(prev => {
      const next = { ...prev, [norm]: { ...prev[norm], customerId, resolution: customerId?"matched":"ignored" } };
      setParsedRows(classifySalesRows(rawRows, next, existingMap));
      return next;
    });
  };

  const readyRows = parsedRows.filter(r=>r.status==="ready");
  const duplicateCount = parsedRows.filter(r=>r.status==="duplicate").length;
  const conflictRows = parsedRows.filter(r=>r.status==="conflict");
  const unresolved = unresolvedList(parsedRows, byNorm);
  const canImport = unresolved.length===0 && (readyRows.length>0 || conflictRows.length>0);

  const doImport = async () => {
    setBusy(true);
    try {
      for (const group of chunk(readyRows,500)) {
        const { error } = await supabase.from("temp_ledger_sales").insert(group.map(r => ({
          txn_date: r.isoDate, invoice_serial: r.invoiceSerial, invoice_no_raw: r.invoiceNoRaw,
          customer_name: r.rawName, customer_id: r.customerId, gst_no: r.gstNo,
          gross_total: r.gross, sales_gst_18: r.salesGst18, sgst_9: r.sgst9, cgst_9: r.cgst9,
          sales_igst_18: r.salesIgst18, igst_18: r.igst18,
          source_file: fileName, uploaded_by: user?.username||null,
        })));
        if (error) throw error;
      }
      for (const r of conflictRows) {
        await supabase.from("temp_ledger_review_queue").insert({
          kind: "sales", txn_date: r.isoDate, customer_id: r.customerId, customer_name_raw: r.rawName,
          new_data: {
            txn_date: r.isoDate, invoice_serial: r.invoiceSerial, invoice_no_raw: r.invoiceNoRaw,
            customer_name: r.rawName, customer_id: r.customerId, gst_no: r.gstNo, gross_total: r.gross,
            sales_gst_18: r.salesGst18, sgst_9: r.sgst9, cgst_9: r.cgst9, sales_igst_18: r.salesIgst18, igst_18: r.igst18,
            source_file: fileName, uploaded_by: user?.username||null,
          },
          existing_ids: [r.existingId],
          conflict_reason: `Invoice ${r.invoiceNoRaw} on ${r.isoDate} already recorded at ${fmt(r.existingGross)}; new file has ${fmt(r.gross)}`,
        });
      }
      setResult({ inserted: readyRows.length, duplicates: duplicateCount, conflicts: conflictRows.length });
      setStep(3);
      onDone && onDone();
    } catch(e) { toast("Import failed: "+e.message,"error"); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:900,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"flex-start",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:12,borderBottom:"1px solid var(--paper2)"}}>
          <div>
            <div style={S.pageTitle}>Upload sales register</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>{["","Upload file","Preview & resolve","Done"][step]}</div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          {step===1 && (
            <div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:13,color:"var(--ink3)"}}>
                Expected columns: <strong>Date · Particulars · Invoice No. · Gross Total</strong> (+ GST columns). Already-imported invoices are detected automatically by date + invoice number.
              </div>
              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>{busy?"Reading file…":"Click to select Excel or CSV file"}</div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
              </div>
            </div>
          )}
          {step===2 && (
            <div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
                {[
                  {label:"Ready to import", value:readyRows.length, color:"var(--accent)"},
                  {label:"Already exists",  value:duplicateCount,   color:"var(--ink3)"},
                  {label:"Conflicts",       value:conflictRows.length, color:"var(--danger)"},
                  {label:"Unresolved names",value:unresolved.length,  color:"var(--warn)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,lineHeight:1}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              {inFileDupCount>0 && (
                <div style={{marginBottom:12,padding:"10px 14px",background:"var(--paper)",borderRadius:8,fontSize:12,color:"var(--ink3)"}}>
                  {inFileDupCount} row{inFileDupCount!==1?"s":""} in the file itself repeated an invoice number already seen earlier in the same file — treated as already-imported.
                </div>
              )}
              <UnresolvedNamesPanel unresolved={unresolved} customers={customers} onResolved={handleNameResolved} onCustomerCreated={cust=>setCustomers(cs=>[...cs,cust])} toast={toast}/>
              {conflictRows.length>0 && (
                <div style={{marginBottom:12,padding:"10px 14px",background:"var(--danger-l)",borderRadius:8,fontSize:12,color:"var(--danger)"}}>
                  {conflictRows.length} row{conflictRows.length!==1?"s":""} have the same invoice already recorded with a different amount — these will be sent to Review Conflicts instead of being imported or overwritten.
                </div>
              )}
              <div style={{maxHeight:360,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Date</th><th style={S.th()}>Customer</th><th style={S.th()}>Invoice No.</th>
                      <th style={S.th(true)}>Gross</th><th style={S.th()}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.slice(0,300).map((r,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)"}}>
                        <td style={S.td}>{r.isoDate}</td>
                        <td style={{...S.td,fontSize:12}}>{r.rawName}</td>
                        <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12}}>{r.invoiceNoRaw}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12}}>{fmt(r.gross)}</td>
                        <td style={S.td}><StatusBadge status={r.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedRows.length>300 && (
                <div style={{padding:"10px 14px",textAlign:"center",fontSize:12,color:"var(--ink3)",borderTop:"1px solid var(--paper2)"}}>
                  Showing first 300 of {parsedRows.length} rows — all classified rows above will be processed on import
                </div>
              )}
              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>← Back</button>
                <button style={S.btnPrimary} onClick={doImport} disabled={busy||!canImport}>
                  {busy?"Importing…":`💾 Import ${readyRows.length + conflictRows.length} row${(readyRows.length+conflictRows.length)!==1?"s":""}`}
                </button>
              </div>
            </div>
          )}
          {step===3 && result && (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:49,marginBottom:12}}>✅</div>
              <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:20}}>Upload complete</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20,maxWidth:420,margin:"0 auto 20px"}}>
                {[
                  {label:"Imported", value:result.inserted, color:"var(--accent)"},
                  {label:"Already existed", value:result.duplicates, color:"var(--ink3)"},
                  {label:"Sent to review", value:result.conflicts, color:"var(--danger)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:25,fontWeight:300,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              <button style={S.btnPrimary} onClick={onClose}>✓ Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── RECEIVABLES UPLOAD MODAL ────────────────────────────────────
function ReceivablesUploadModal({ onClose, onDone, toast }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [existingRows, setExistingRows] = useState([]);
  const [byNorm, setByNorm] = useState({});
  const [parsedRows, setParsedRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true); setFileName(file.name);
    try {
      const text = await loadXlsxOrCsvText(file);
      const startIdx = detectHeaderStart(text, ["name","amount"]);
      const trimmedText = text.split(/\r?\n/).slice(startIdx).join("\n");
      const { headers, rows } = parseCSV(trimmedText);
      const findCol = (pred) => headers.find(h => pred(h.toLowerCase().trim()));
      const snoCol = findCol(h=>h==="s.no"||h==="sno"||h==="s no");
      const dateCol = findCol(h=>h==="date");
      const nameCol = findCol(h=>h==="name");
      const amountCol = findCol(h=>h==="amount");
      const methodCol = findCol(h=>h==="method");

      const parsed = rows.map(row => {
        const isoDate = parseDate(dateCol?row[dateCol]:"");
        const rawName = (nameCol?row[nameCol]:"").trim();
        const amount = numVal(amountCol?row[amountCol]:0);
        const methodRaw = (methodCol?row[methodCol]:"").trim();
        return {
          isoDate, rawName, norm: normalizeName(rawName), amount,
          methodRaw, methodNormalized: methodRaw.toLowerCase().trim(),
          snoRaw: snoCol ? (row[snoCol]||"").trim() : "",
          valid: !!(isoDate && rawName && amount>0 && methodRaw),
        };
      }).filter(r => r.isoDate || r.rawName);

      const validRows = parsed.filter(r=>r.valid);
      if (!validRows.length) { toast("No valid rows found — check the file has Date/Name/Amount/Method columns.","error"); setBusy(false); return; }

      const custs = customers.length ? customers : await loadActiveCustomers();
      if (!customers.length) setCustomers(custs);
      const nameMap = await resolveNames(validRows.map(r=>r.rawName), custs);

      const resolvableDates = validRows.filter(r=>nameMap[r.norm]?.resolution==="matched").map(r=>r.isoDate).sort();
      const minDate = resolvableDates[0], maxDate = resolvableDates[resolvableDates.length-1];
      const existing = minDate ? await fetchAllPaged(() => supabase.from("temp_ledger_receivables")
        .select("id,txn_date,customer_id,amount,method_normalized")
        .gte("txn_date",minDate).lte("txn_date",maxDate)) : [];

      setRawRows(validRows);
      setExistingRows(existing);
      setByNorm(nameMap);
      setParsedRows(classifyReceivablesRows(validRows, nameMap, existing));
      setStep(2);
    } catch(e) {
      toast("Failed to read file: "+e.message,"error");
    } finally { setBusy(false); }
  };

  const handleNameResolved = (norm, customerId) => {
    setByNorm(prev => {
      const next = { ...prev, [norm]: { ...prev[norm], customerId, resolution: customerId?"matched":"ignored" } };
      setParsedRows(classifyReceivablesRows(rawRows, next, existingRows));
      return next;
    });
  };

  const readyRows = parsedRows.filter(r=>r.status==="ready");
  const duplicateCount = parsedRows.filter(r=>r.status==="duplicate").length;
  const conflictRows = parsedRows.filter(r=>r.status==="conflict");
  const unresolved = unresolvedList(parsedRows, byNorm);
  const canImport = unresolved.length===0 && (readyRows.length>0 || conflictRows.length>0);

  const doImport = async () => {
    setBusy(true);
    try {
      for (const group of chunk(readyRows,500)) {
        const { error } = await supabase.from("temp_ledger_receivables").insert(group.map(r => ({
          txn_date: r.isoDate, customer_name: r.rawName, customer_id: r.customerId,
          amount: r.amount, method_raw: r.methodRaw, method_normalized: r.methodNormalized,
          source_row_sno: r.snoRaw ? (parseInt(r.snoRaw)||null) : null,
          source_file: fileName, uploaded_by: user?.username||null,
        })));
        if (error) throw error;
      }
      for (const r of conflictRows) {
        await supabase.from("temp_ledger_review_queue").insert({
          kind: "receivables", txn_date: r.isoDate, customer_id: r.customerId, customer_name_raw: r.rawName,
          new_data: {
            txn_date: r.isoDate, customer_name: r.rawName, customer_id: r.customerId, amount: r.amount,
            method_raw: r.methodRaw, method_normalized: r.methodNormalized,
            source_row_sno: r.snoRaw ? (parseInt(r.snoRaw)||null) : null,
            source_file: fileName, uploaded_by: user?.username||null,
          },
          existing_ids: r.conflictExistingIds,
          conflict_reason: `${r.rawName} on ${r.isoDate} via ${r.methodRaw} already has ${r.conflictExistingAmounts.map(a=>fmt(a)).join(", ")} on record; new file has ${fmt(r.amount)}`,
        });
      }
      setResult({ inserted: readyRows.length, duplicates: duplicateCount, conflicts: conflictRows.length });
      setStep(3);
      onDone && onDone();
    } catch(e) { toast("Import failed: "+e.message,"error"); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:900,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"flex-start",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:12,borderBottom:"1px solid var(--paper2)"}}>
          <div>
            <div style={S.pageTitle}>Upload receivables</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>{["","Upload file","Preview & resolve","Done"][step]}</div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          {step===1 && (
            <div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:13,color:"var(--ink3)"}}>
                Expected columns: <strong>Date · Name · Amount · Method</strong>. There's no reference number in this format, so duplicates are detected by counting matching (date, customer, amount, method) combinations — genuine repeat same-day payments import correctly, exact re-uploads don't.
              </div>
              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>{busy?"Reading file…":"Click to select Excel or CSV file"}</div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
              </div>
            </div>
          )}
          {step===2 && (
            <div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:16}}>
                {[
                  {label:"Ready to import", value:readyRows.length, color:"var(--accent)"},
                  {label:"Already exists",  value:duplicateCount,   color:"var(--ink3)"},
                  {label:"Conflicts",       value:conflictRows.length, color:"var(--danger)"},
                  {label:"Unresolved names",value:unresolved.length,  color:"var(--warn)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300,lineHeight:1}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              <UnresolvedNamesPanel unresolved={unresolved} customers={customers} onResolved={handleNameResolved} onCustomerCreated={cust=>setCustomers(cs=>[...cs,cust])} toast={toast}/>
              {conflictRows.length>0 && (
                <div style={{marginBottom:12,padding:"10px 14px",background:"var(--danger-l)",borderRadius:8,fontSize:12,color:"var(--danger)"}}>
                  {conflictRows.length} row{conflictRows.length!==1?"s":""} have the same date/customer/method already on record with a different amount — these will be sent to Review Conflicts instead of being imported or overwritten.
                </div>
              )}
              <div style={{maxHeight:360,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                      <th style={S.th()}>Date</th><th style={S.th()}>Customer</th><th style={S.th()}>Method</th>
                      <th style={S.th(true)}>Amount</th><th style={S.th()}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedRows.slice(0,300).map((r,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)"}}>
                        <td style={S.td}>{r.isoDate}</td>
                        <td style={{...S.td,fontSize:12}}>{r.rawName}</td>
                        <td style={{...S.td,fontSize:12}}>{r.methodRaw}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12}}>{fmt(r.amount)}</td>
                        <td style={S.td}><StatusBadge status={r.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedRows.length>300 && (
                <div style={{padding:"10px 14px",textAlign:"center",fontSize:12,color:"var(--ink3)",borderTop:"1px solid var(--paper2)"}}>
                  Showing first 300 of {parsedRows.length} rows — all classified rows above will be processed on import
                </div>
              )}
              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>← Back</button>
                <button style={S.btnPrimary} onClick={doImport} disabled={busy||!canImport}>
                  {busy?"Importing…":`💾 Import ${readyRows.length + conflictRows.length} row${(readyRows.length+conflictRows.length)!==1?"s":""}`}
                </button>
              </div>
            </div>
          )}
          {step===3 && result && (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:49,marginBottom:12}}>✅</div>
              <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:20}}>Upload complete</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20,maxWidth:420,margin:"0 auto 20px"}}>
                {[
                  {label:"Imported", value:result.inserted, color:"var(--accent)"},
                  {label:"Already existed", value:result.duplicates, color:"var(--ink3)"},
                  {label:"Sent to review", value:result.conflicts, color:"var(--danger)"},
                ].map(s=>(
                  <div key={s.label} style={S.statCard(s.color)}>
                    <div style={{fontFamily:"var(--serif)",fontSize:25,fontWeight:300,color:s.color}}>{s.value}</div>
                    <div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
                  </div>
                ))}
              </div>
              <button style={S.btnPrimary} onClick={onClose}>✓ Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── OPENING BALANCES ────────────────────────────────────────────
function TempOpeningBalanceModal({ customer, existing, onClose, onSave, toast }) {
  const [asOfDate, setAsOfDate] = useState(existing?.as_of_date || new Date().toISOString().slice(0,10));
  const [debit, setDebit] = useState(existing?.opening_debit ?? "");
  const [credit, setCredit] = useState(existing?.opening_credit ?? "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { error } = await supabase.from("temp_ledger_opening_balances").upsert({
        customer_id: customer.id, as_of_date: asOfDate,
        opening_debit: parseFloat(debit)||0, opening_credit: parseFloat(credit)||0,
        notes: notes||null, updated_at: new Date().toISOString(),
      }, { onConflict: "customer_id" });
      if (error) throw error;
      toast("Opening balance saved.");
      onSave();
    } catch(e) { toast("Error: "+e.message,"error"); }
    finally { setSaving(false); }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:420}}>
        <div style={{padding:"20px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={S.pageTitle}>{customer.name}</div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"16px 24px 24px"}}>
          <label style={S.label}>As of date</label>
          <input type="date" value={asOfDate} onChange={e=>setAsOfDate(e.target.value)} style={{...S.input,marginBottom:14}}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <div><label style={S.label}>Opening debit (owed)</label><input type="number" value={debit} onChange={e=>setDebit(e.target.value)} style={S.input}/></div>
            <div><label style={S.label}>Opening credit (paid)</label><input type="number" value={credit} onChange={e=>setCredit(e.target.value)} style={S.input}/></div>
          </div>
          <label style={S.label}>Notes</label>
          <input value={notes} onChange={e=>setNotes(e.target.value)} style={{...S.input,marginBottom:18}}/>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={save} disabled={saving}>{saving?"Saving…":"Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TempBulkOpeningBalanceModal({ customers: customersProp, onClose, onSave, toast }) {
  const [step, setStep] = useState(1);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0,10));
  const [customers, setCustomers] = useState(customersProp||[]);
  const [rawRows, setRawRows] = useState([]);
  const [byNorm, setByNorm] = useState({});
  const [preview, setPreview] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const classify = (rows, bn) => rows.map(r => {
    const nm = bn[r.norm] || { customerId:null, resolution:"unresolved" };
    return { ...r, customerId: nm.customerId, resolution: nm.resolution };
  });

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const text = await loadXlsxOrCsvText(file);
      const { headers, rows } = parseCSV(text);
      const findCol = (pred) => headers.find(h => pred(h.toLowerCase().trim()));
      const nameCol = findCol(h=>h.includes("name"));
      const debitCol = findCol(h=>h.includes("debit")||h.includes("owed")||h.includes("sale"));
      const creditCol = findCol(h=>h.includes("credit")||h.includes("paid")||h.includes("received"));
      const rawNames = rows.map(r=>(nameCol?r[nameCol]:"").trim()).filter(Boolean);
      const bn = await resolveNames(rawNames, customers);
      const rr = rows.map(row => {
        const raw = (nameCol?row[nameCol]:"").trim();
        return { raw, norm: normalizeName(raw),
          debit: numVal(debitCol?row[debitCol]:0), credit: numVal(creditCol?row[creditCol]:0) };
      }).filter(r=>r.raw);
      setRawRows(rr);
      setByNorm(bn);
      setPreview(classify(rr, bn));
      setStep(2);
    } catch(e) { toast("Failed to read file: "+e.message,"error"); }
    finally { setBusy(false); }
  };

  const handleNameResolved = (norm, customerId) => {
    const next = { ...byNorm, [norm]: { ...byNorm[norm], customerId, resolution: customerId?"matched":"ignored" } };
    setByNorm(next);
    setPreview(classify(rawRows, next));
  };

  const ready = preview.filter(r=>r.resolution==="matched");
  const unmatched = preview.filter(r=>r.resolution==="unresolved");
  const unresolved = unresolvedList(preview, byNorm);

  const doSave = async () => {
    setBusy(true);
    let saved=0; const errors=[];
    for (const group of chunk(ready,200)) {
      const { error } = await supabase.from("temp_ledger_opening_balances").upsert(group.map(r => ({
        customer_id: r.customerId, as_of_date: asOfDate, opening_debit: r.debit, opening_credit: r.credit,
        notes: "Bulk upload", updated_at: new Date().toISOString(),
      })), { onConflict: "customer_id" });
      if (error) errors.push(error.message); else saved += group.length;
    }
    setResult({ saved, unmatched: unmatched.length, errors });
    setBusy(false);
    setStep(3);
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:720,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",justifyContent:"space-between",alignItems:"flex-start",borderBottom:"1px solid var(--paper2)",paddingBottom:12}}>
          <div>
            <div style={S.pageTitle}>Bulk opening balances</div>
            <div style={{fontSize:12,color:"var(--ink3)",marginTop:4}}>{["","Upload file","Preview","Done"][step]}</div>
          </div>
          <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
        </div>
        <div style={{padding:"20px 28px 28px"}}>
          {step===1 && (
            <div>
              <div style={{marginBottom:20,padding:16,background:"var(--info-l)",borderRadius:8}}>
                <label style={S.label}>As of date * — applied to all rows</label>
                <input type="date" value={asOfDate} onChange={e=>setAsOfDate(e.target.value)} style={{...S.input,maxWidth:220}}/>
              </div>
              <div style={{marginBottom:16,padding:"12px 16px",background:"var(--paper)",borderRadius:8,fontSize:13,color:"var(--ink3)"}}>
                Columns expected: <strong>Customer name · Opening debit · Opening credit</strong>
              </div>
              <div style={{border:"2px dashed var(--paper3)",borderRadius:10,padding:48,textAlign:"center",cursor:"pointer",background:"var(--paper)"}}
                onClick={()=>fileRef.current?.click()}>
                <div style={{fontSize:37,marginBottom:8}}>📂</div>
                <div style={{fontSize:14,fontWeight:500,color:"var(--ink2)"}}>{busy?"Reading file…":"Click to select Excel or CSV file"}</div>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
              </div>
            </div>
          )}
          {step===2 && (
            <div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,marginBottom:16}}>
                <div style={S.statCard("var(--accent)")}><div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300}}>{ready.length}</div><div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6}}>Ready</div></div>
                <div style={S.statCard("var(--warn)")}><div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300}}>{unmatched.length}</div><div style={{fontSize:11,color:"var(--ink3)",textTransform:"uppercase",marginTop:6}}>No match</div></div>
              </div>
              <UnresolvedNamesPanel unresolved={unresolved} customers={customers} onResolved={handleNameResolved} onCustomerCreated={cust=>setCustomers(cs=>[...cs,cust])} toast={toast}/>
              <div style={{maxHeight:340,overflowY:"auto",border:"1px solid var(--paper3)",borderRadius:8,marginBottom:16}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr style={{background:"var(--paper2)"}}><th style={S.th()}>Name</th><th style={S.th(true)}>Debit</th><th style={S.th(true)}>Credit</th><th style={S.th()}>Status</th></tr></thead>
                  <tbody>
                    {preview.slice(0,300).map((r,i)=>(
                      <tr key={i} style={{borderBottom:"1px solid var(--paper2)"}}>
                        <td style={{...S.td,fontSize:12}}>{r.raw}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12}}>{fmt(r.debit)}</td>
                        <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontSize:12}}>{fmt(r.credit)}</td>
                        <td style={S.td}>{r.resolution==="matched"?<StatusBadge status="ready"/>:<StatusBadge status="unmatched"/>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"space-between"}}>
                <button style={S.btnGhost} onClick={()=>setStep(1)}>← Back</button>
                <button style={S.btnPrimary} onClick={doSave} disabled={busy||ready.length===0}>{busy?"Saving…":`💾 Save ${ready.length}`}</button>
              </div>
            </div>
          )}
          {step===3 && result && (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:49,marginBottom:12}}>✅</div>
              <div style={{fontFamily:"var(--serif)",fontSize:23,fontWeight:300,marginBottom:20}}>Done — {result.saved} saved{result.unmatched?`, ${result.unmatched} unmatched`:""}</div>
              <button style={S.btnPrimary} onClick={onSave}>✓ Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OpeningBalanceTab({ customers, toast }) {
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [editCustomer, setEditCustomer] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const rows = await fetchAllPaged(() => supabase.from("temp_ledger_opening_balances").select("*"));
    const m = {}; rows.forEach(r => { m[r.customer_id] = r; });
    setBalances(m); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const withBalance = customers.filter(c => balances[c.id]);

  return (
    <div>
      <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center"}}>
        <div style={{flex:1,maxWidth:320}}>
          <AutoComplete value={pickerQuery} onChange={setPickerQuery}
            onSelect={c=>{ setEditCustomer(c); setPickerQuery(""); }}
            items={customers} placeholder="Search customer to add/edit opening balance…"
            getKey={c=>c.id} getLabel={c=>c.name} renderItem={c=><div style={{fontSize:13}}>{c.name}</div>}/>
        </div>
        <button style={S.btnGhost} onClick={()=>setBulkOpen(true)}>⬆ Bulk upload</button>
      </div>
      <div style={S.card}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"var(--paper2)"}}>
            <th style={S.th()}>Customer</th><th style={S.th()}>As of</th>
            <th style={S.th(true)}>Opening debit</th><th style={S.th(true)}>Opening credit</th>
            <th style={S.th(true)}>Net</th><th style={S.th()}></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{...S.td,textAlign:"center",padding:30}}>Loading…</td></tr> :
             withBalance.length===0 ? <tr><td colSpan={6} style={{...S.td,textAlign:"center",padding:30,color:"var(--ink3)"}}>No opening balances entered yet.</td></tr> :
             withBalance.map(c => {
              const b = balances[c.id]; const net = parseFloat(b.opening_debit)-parseFloat(b.opening_credit);
              return (
                <tr key={c.id} style={{borderBottom:"1px solid var(--paper2)",cursor:"pointer"}} onClick={()=>setEditCustomer(c)}>
                  <td style={{...S.td,fontWeight:500}}>{c.name}</td>
                  <td style={S.td}>{fmtDMY(b.as_of_date)}</td>
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(b.opening_debit)}</td>
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(b.opening_credit)}</td>
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)",fontWeight:500,color:net>0?"var(--danger)":"var(--accent)"}}>{fmt(Math.abs(net))}{net<0?" Cr":""}</td>
                  <td style={{...S.td,textAlign:"right"}}><button style={S.btnSm} onClick={e=>{e.stopPropagation();setEditCustomer(c);}}>Edit</button></td>
                </tr>
              );
             })}
          </tbody>
        </table>
      </div>
      {editCustomer && <TempOpeningBalanceModal customer={editCustomer} existing={balances[editCustomer.id]} onClose={()=>setEditCustomer(null)} onSave={()=>{setEditCustomer(null);load();}} toast={toast}/>}
      {bulkOpen && <TempBulkOpeningBalanceModal customers={customers} onClose={()=>setBulkOpen(false)} onSave={()=>{setBulkOpen(false);load();}} toast={toast}/>}
    </div>
  );
}

// ── REVIEW CONFLICTS ─────────────────────────────────────────────
function ReviewConflictsTab({ toast }) {
  const [rows, setRows] = useState([]);
  const [existingMap, setExistingMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("temp_ledger_review_queue").select("*").eq("status","pending").order("created_at",{ascending:false});
    const list = data||[];
    setRows(list);
    const salesIds = [...new Set(list.filter(r=>r.kind==="sales").flatMap(r=>r.existing_ids||[]))];
    const recvIds = [...new Set(list.filter(r=>r.kind==="receivables").flatMap(r=>r.existing_ids||[]))];
    const em = {};
    if (salesIds.length) {
      const { data:s } = await supabase.from("temp_ledger_sales").select("*").in("id",salesIds);
      (s||[]).forEach(r=>{ em[r.id]=r; });
    }
    if (recvIds.length) {
      const { data:rc } = await supabase.from("temp_ledger_receivables").select("*").in("id",recvIds);
      (rc||[]).forEach(r=>{ em[r.id]=r; });
    }
    setExistingMap(em);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (row, action) => {
    setBusyId(row.id);
    try {
      if (action==="update") {
        const targetId = row.existing_ids[0];
        if (row.kind==="sales") {
          await supabase.from("temp_ledger_sales").update({
            gross_total: row.new_data.gross_total, sales_gst_18: row.new_data.sales_gst_18,
            sgst_9: row.new_data.sgst_9, cgst_9: row.new_data.cgst_9,
            sales_igst_18: row.new_data.sales_igst_18, igst_18: row.new_data.igst_18,
            gst_no: row.new_data.gst_no,
          }).eq("id",targetId);
        } else {
          await supabase.from("temp_ledger_receivables").update({ amount: row.new_data.amount }).eq("id",targetId);
        }
        await supabase.from("temp_ledger_review_queue").update({ status:"resolved_update", resolved_at:new Date().toISOString() }).eq("id",row.id);
      } else if (action==="keep") {
        const table = row.kind==="sales" ? "temp_ledger_sales" : "temp_ledger_receivables";
        await supabase.from(table).insert(row.new_data);
        await supabase.from("temp_ledger_review_queue").update({ status:"resolved_keep_both", resolved_at:new Date().toISOString() }).eq("id",row.id);
      } else {
        await supabase.from("temp_ledger_review_queue").update({ status:"resolved_ignored", resolved_at:new Date().toISOString() }).eq("id",row.id);
      }
      toast("Resolved.");
      load();
    } catch(e) { toast("Error: "+e.message,"error"); }
    finally { setBusyId(null); }
  };

  if (loading) return <div style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>Loading…</div>;
  if (!rows.length) return <div style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>No conflicts pending review.</div>;

  return (
    <div>
      {rows.map(row => {
        const rowExisting = (row.existing_ids||[]).map(id=>existingMap[id]).filter(Boolean);
        return (
          <div key={row.id} style={{...S.card,padding:18,marginBottom:14}}>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:13,fontWeight:500}}>{row.customer_name_raw} · {fmtDMY(row.txn_date)} · {row.kind==="sales"?"Sale":"Receivable"}</div>
              <div style={{fontSize:12,color:"var(--warn)",marginTop:4}}>{row.conflict_reason}</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14,fontSize:12}}>
              <div style={{padding:10,background:"var(--paper)",borderRadius:8}}>
                <div style={{color:"var(--ink3)",marginBottom:4,textTransform:"uppercase",fontSize:11}}>Existing on record</div>
                {rowExisting.map(e=><div key={e.id}>{row.kind==="sales"?fmt(e.gross_total):fmt(e.amount)}</div>)}
              </div>
              <div style={{padding:10,background:"var(--info-l)",borderRadius:8}}>
                <div style={{color:"var(--info)",marginBottom:4,textTransform:"uppercase",fontSize:11}}>New in file</div>
                <div>{row.kind==="sales"?fmt(row.new_data.gross_total):fmt(row.new_data.amount)}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              {rowExisting.length===1 && <button style={S.btnPrimary} disabled={busyId===row.id} onClick={()=>resolve(row,"update")}>Update existing</button>}
              {row.kind==="receivables" && <button style={S.btnGhost} disabled={busyId===row.id} onClick={()=>resolve(row,"keep")}>Keep both</button>}
              <button style={S.btnDanger} disabled={busyId===row.id} onClick={()=>resolve(row,"ignore")}>Ignore new</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── LEDGER VIEW ───────────────────────────────────────────────────
function currentFYStart() {
  const now = new Date();
  const y = now.getMonth()>=3 ? now.getFullYear() : now.getFullYear()-1;
  return `${y}-04-01`;
}

function LedgerView({ customers }) {
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState(null);
  const [from, setFrom] = useState(currentFYStart());
  const [to, setTo] = useState(new Date().toISOString().slice(0,10));
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (!customer) return;
    setLoading(true);
    try {
      const [obRes, priorSales, priorReceipts, salesInRange, receiptsInRange] = await Promise.all([
        supabase.from("temp_ledger_opening_balances").select("*").eq("customer_id",customer.id).maybeSingle(),
        fetchAllPaged(() => supabase.from("temp_ledger_sales").select("gross_total").eq("customer_id",customer.id).lt("txn_date",from)),
        fetchAllPaged(() => supabase.from("temp_ledger_receivables").select("amount").eq("customer_id",customer.id).lt("txn_date",from)),
        fetchAllPaged(() => supabase.from("temp_ledger_sales").select("txn_date,invoice_no_raw,gross_total").eq("customer_id",customer.id).gte("txn_date",from).lte("txn_date",to)),
        fetchAllPaged(() => supabase.from("temp_ledger_receivables").select("txn_date,method_raw,amount").eq("customer_id",customer.id).gte("txn_date",from).lte("txn_date",to)),
      ]);
      const ob = obRes.data;
      const priorSalesSum = priorSales.reduce((s,r)=>s+parseFloat(r.gross_total||0),0);
      const priorReceiptsSum = priorReceipts.reduce((s,r)=>s+parseFloat(r.amount||0),0);
      const opening = (parseFloat(ob?.opening_debit||0) - parseFloat(ob?.opening_credit||0)) + priorSalesSum - priorReceiptsSum;

      const merged = [
        ...salesInRange.map(s=>({date:s.txn_date, vchType:"Sale", vchNo:s.invoice_no_raw, debit:parseFloat(s.gross_total||0), credit:0, sortW:0})),
        ...receiptsInRange.map(r=>({date:r.txn_date, vchType:"Receipt", vchNo:r.method_raw, debit:0, credit:parseFloat(r.amount||0), sortW:1})),
      ].sort((a,b)=> a.date===b.date ? a.sortW-b.sortW : (a.date<b.date?-1:1));

      let bal = opening;
      merged.forEach(e => { bal += e.debit - e.credit; e.balance = bal; });

      setOpeningBalance(opening);
      setEntries(merged);
      setLoaded(true);
    } catch(e) { alert("Error loading ledger: "+e.message); }
    finally { setLoading(false); }
  };

  const closingBalance = entries.length ? entries[entries.length-1].balance : openingBalance;
  const totalDebit = entries.reduce((s,e)=>s+e.debit,0);
  const totalCredit = entries.reduce((s,e)=>s+e.credit,0);

  const downloadCSV = () => {
    const lines = [];
    lines.push([`Ledger Statement — ${customer.name}`]);
    lines.push([`Period: ${fmtDMY(from)} to ${fmtDMY(to)}`]);
    lines.push([`Generated: ${new Date().toLocaleDateString("en-IN")}`]);
    lines.push([]);
    lines.push(["Date","Particulars","Vch Type","Vch No.","Debit","Credit","Balance"]);
    lines.push([fmtDMY(from),"Opening Balance","","","","",parseFloat(Math.abs(openingBalance)).toFixed(2)+(openingBalance<0?" Cr":"")]);
    entries.forEach(e => {
      lines.push([fmtDMY(e.date), customer.name, e.vchType, e.vchNo||"", e.debit?e.debit.toFixed(2):"", e.credit?e.credit.toFixed(2):"", parseFloat(Math.abs(e.balance)).toFixed(2)+(e.balance<0?" Cr":"")]);
    });
    lines.push(["","","","",totalDebit.toFixed(2),totalCredit.toFixed(2),""]);
    lines.push([fmtDMY(to),"Closing Balance","","","","",parseFloat(Math.abs(closingBalance)).toFixed(2)+(closingBalance<0?" Cr":"")]);
    const csv = lines.map(r=>r.map(v=>`"${String(v??"").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href=url; link.download=`ledger-${customer.name.replace(/\s+/g,"-")}-${from}-to-${to}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{display:"flex",gap:12,alignItems:"flex-end",marginBottom:20,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:240}}>
          <label style={S.label}>Customer</label>
          <AutoComplete value={customer?customer.name:query} onChange={v=>{setQuery(v);setCustomer(null);setLoaded(false);}}
            onSelect={c=>{setCustomer(c);setQuery(c.name);}} items={customers}
            placeholder="Search customer…" getKey={c=>c.id} getLabel={c=>c.name}
            renderItem={c=><div style={{fontSize:13}}>{c.name}</div>}/>
        </div>
        <div><label style={S.label}>From</label><input type="date" value={from} onChange={e=>setFrom(e.target.value)} style={S.input}/></div>
        <div><label style={S.label}>To</label><input type="date" value={to} onChange={e=>setTo(e.target.value)} style={S.input}/></div>
        <button style={S.btnPrimary} onClick={load} disabled={!customer||loading}>{loading?"Loading…":"Load ledger"}</button>
        {loaded && <>
          <button style={S.btnGhost} onClick={downloadCSV}>⬇ CSV</button>
          <button style={S.btnGhost} onClick={()=>window.print()}>🖨 Print (A4)</button>
        </>}
      </div>

      {loaded && customer && (
        <div className="temp-ledger-print">
          <style>{`
            @media print{
              @page{size:A4;margin:14mm;}
              body *{visibility:hidden;height:0!important;overflow:hidden!important;}
              .temp-ledger-print,.temp-ledger-print *{visibility:visible;height:auto!important;overflow:visible!important;}
              .temp-ledger-print{position:fixed;top:0;left:0;width:100%;padding:0;}
            }
          `}</style>
          <div style={{marginBottom:16}}>
            <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:300}}>{COMPANY.name}</div>
            <div style={{fontSize:12,color:"var(--ink3)"}}>{COMPANY.address}</div>
            <div style={{fontFamily:"var(--serif)",fontSize:17,fontWeight:300,marginTop:12}}>Ledger Account — {customer.name}</div>
            <div style={{fontSize:12,color:"var(--ink3)"}}>{fmtDMY(from)} to {fmtDMY(to)}</div>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{borderBottom:"2px solid var(--ink)"}}>
                <th style={S.th()}>Date</th><th style={S.th()}>Particulars</th><th style={S.th()}>Vch Type</th>
                <th style={S.th()}>Vch No.</th><th style={S.th(true)}>Debit</th><th style={S.th(true)}>Credit</th><th style={S.th(true)}>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{borderBottom:"1px solid var(--paper2)",fontWeight:500}}>
                <td style={S.td}>{fmtDMY(from)}</td><td style={S.td}>Opening Balance</td><td style={S.td}></td><td style={S.td}></td>
                <td style={S.td}></td><td style={S.td}></td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(Math.abs(openingBalance))}{openingBalance<0?" Cr":openingBalance>0?" Dr":""}</td>
              </tr>
              {entries.map((e,i) => (
                <tr key={i} style={{borderBottom:"1px solid var(--paper2)"}}>
                  <td style={S.td}>{fmtDMY(e.date)}</td>
                  <td style={S.td}>{customer.name}</td>
                  <td style={S.td}>{e.vchType}</td>
                  <td style={S.td}>{e.vchNo||"—"}</td>
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{e.debit?fmt(e.debit):""}</td>
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{e.credit?fmt(e.credit):""}</td>
                  <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(Math.abs(e.balance))}{e.balance<0?" Cr":e.balance>0?" Dr":""}</td>
                </tr>
              ))}
              <tr style={{borderTop:"2px solid var(--ink)",fontWeight:500}}>
                <td style={S.td} colSpan={4}>Total</td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(totalDebit)}</td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(totalCredit)}</td>
                <td style={S.td}></td>
              </tr>
              <tr style={{fontWeight:600}}>
                <td style={S.td}>{fmtDMY(to)}</td><td style={S.td}>Closing Balance</td><td style={S.td}></td><td style={S.td}></td>
                <td style={S.td}></td><td style={S.td}></td>
                <td style={{...S.td,textAlign:"right",fontFamily:"var(--mono)"}}>{fmt(Math.abs(closingBalance))}{closingBalance<0?" Cr":closingBalance>0?" Dr":""}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {!loaded && <div style={{padding:60,textAlign:"center",color:"var(--ink3)",fontSize:13}}>Select a customer and click "Load ledger" to view their statement.</div>}
    </div>
  );
}

// ── PAGE ─────────────────────────────────────────────────────────
export function TempLedgerPage({ toast }) {
  const [activeTab, setActiveTab] = useState("sales");
  const [customers, setCustomers] = useState([]);
  const [salesModal, setSalesModal] = useState(false);
  const [recvModal, setRecvModal] = useState(false);
  const [pendingConflicts, setPendingConflicts] = useState(0);

  const loadCustomers = useCallback(async () => { setCustomers(await loadActiveCustomers()); }, []);
  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const refreshConflictCount = useCallback(async () => {
    const { count } = await supabase.from("temp_ledger_review_queue").select("id",{count:"exact",head:true}).eq("status","pending");
    setPendingConflicts(count||0);
  }, []);
  useEffect(() => { refreshConflictCount(); }, [refreshConflictCount, activeTab]);

  const tabs = [
    { key:"sales", label:"Upload sales" },
    { key:"receivables", label:"Upload receivables" },
    { key:"opening", label:"Opening balances" },
    { key:"conflicts", label:`Review conflicts${pendingConflicts?` (${pendingConflicts})`:""}` },
    { key:"ledger", label:"Ledger statement" },
  ];

  return (
    <div>
      <div style={S.pageHeader}>
        <div>
          <div style={S.pageTitle}>Temp Ledger</div>
          <div style={S.subtitle}>Standalone migration ledger — old-system sales & receivables, kept separate from the main app's accounting</div>
        </div>
      </div>

      <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:"1px solid var(--paper3)",flexWrap:"wrap"}}>
        {tabs.map(t => (
          <button key={t.key} onClick={()=>setActiveTab(t.key)}
            style={{padding:"10px 20px",fontSize:13,cursor:"pointer",border:"none",background:"transparent",fontFamily:"var(--mono)",
              borderBottom:`2px solid ${activeTab===t.key?"var(--accent)":"transparent"}`,
              color:activeTab===t.key?"var(--accent)":"var(--ink3)",fontWeight:activeTab===t.key?500:400}}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab==="sales" && (
        <div>
          <div style={{marginBottom:16}}>
            <button style={S.btnPrimary} onClick={()=>setSalesModal(true)}>⬆ Upload sales register</button>
          </div>
          <div style={{fontSize:13,color:"var(--ink3)"}}>Upload the old system's Sales register export (Date, Particulars, Invoice No., Gross Total, GST columns). Already-imported invoices are detected automatically and skipped.</div>
        </div>
      )}
      {activeTab==="receivables" && (
        <div>
          <div style={{marginBottom:16}}>
            <button style={S.btnPrimary} onClick={()=>setRecvModal(true)}>⬆ Upload receivables</button>
          </div>
          <div style={{fontSize:13,color:"var(--ink3)"}}>Upload the old system's Receivables/payments export (Date, Name, Amount, Method). Already-imported payments are detected automatically, including legitimate repeat same-day payments.</div>
        </div>
      )}
      {activeTab==="opening" && <OpeningBalanceTab customers={customers} toast={toast}/>}
      {activeTab==="conflicts" && <ReviewConflictsTab toast={toast}/>}
      {activeTab==="ledger" && <LedgerView customers={customers}/>}

      {salesModal && <SalesUploadModal onClose={()=>setSalesModal(false)} toast={toast} onDone={refreshConflictCount}/>}
      {recvModal && <ReceivablesUploadModal onClose={()=>setRecvModal(false)} toast={toast} onDone={refreshConflictCount}/>}
    </div>
  );
}
